import emailMisspelled, { top100 } from 'email-misspelled';

import { getParsedPhoneNumber } from './phone';

// Commonly used japanese domains
const JP_DOMAINS = [
  'icloud.com',
  'docomo.ne.jp',
  'ezweb.ne.jp',
  'yahoo.co.jp',
  'au.com',
];
const domainList = [...new Set([...top100, ...JP_DOMAINS])];

const emailChecker = emailMisspelled({ maxMisspelled: 1, domains: domainList });

const mustBeInValidDateOfBirthRange = (val: string) => {
  const enteredDate = new Date(val);
  const today = new Date();

  const todayAtMidnight = new Date(
    `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`
  );

  const minimumDateAtMidnight = new Date(todayAtMidnight);

  // Minimum date is 110 years before now
  minimumDateAtMidnight.setFullYear(todayAtMidnight.getFullYear() - 110);

  const maximumDateAtMidnight = new Date(todayAtMidnight);

  // Maximum date is 18 years before now
  maximumDateAtMidnight.setFullYear(todayAtMidnight.getFullYear() - 18);

  if (enteredDate.getTime() > todayAtMidnight.getTime()) {
    // future DoB
    return 'エラー：無効な日付です';
  }

  if (enteredDate.getTime() < minimumDateAtMidnight.getTime()) {
    // too old
    return '年齢制限の範囲外です';
  }

  if (enteredDate.getTime() > maximumDateAtMidnight.getTime()) {
    // too young
    return '18歳以上の方のみご利用になれます';
  }

  return true;
};

const mustBeValidPhone = (val: string) => {
  const parsedNumber = getParsedPhoneNumber(val);

  // A valid international JP mobile number should start at 81
  // and have 12 characters
  const isValid =
    parsedNumber.indexOf('81') === 0 && parsedNumber.length === 12;

  return isValid || '電話番号を正しく入力してください';
};

const rules = {
  mustNotBeEmptyOrSpace: (val: string) =>
    (!!val && !!val.trim()) || '必要項目です',
  mustNotContainSpace: (val: string) =>
    !val || /^\S*$/.test(val) || '入力に誤りがあります',
  mustNotContainNonPrintableChars: (val: string) =>
    // eslint-disable-next-line no-control-regex
    !val || !/[\x00-\x1F]/.test(val) || '入力に誤りがあります',
  mustBeKatakana: (val: string) =>
    !val ||
    /^([\u30A0-\u30FF]|[\uFF65-\uFF9F])*$/.test(val) ||
    'カタカナで入力してください',
  mustBeKatakanaOrSpaces: (val: string) =>
    !val ||
    /^([\u30A0-\u30FF]|[\uFF65-\uFF9F]|\s)*$/.test(val) ||
    'カタカナで入力してください',
  mustBeFullWidthKatakanaAlphabetSpaces: (val: string) =>
    !val ||
    /^([\u30A0-\u30FF]|([\uFF21-\uFF5A])|\s)*$/.test(val) ||
    '全角カタカナもしくは全角英数字で入力してください',
  mustBeZipCode: (val: string) =>
    !val || /^\d{7}$/.test(val) || '7桁で入力してください',
  mustBeNumber: (val: string) =>
    !val || /^\d+$/.test(val) || '数字で入力してください',
  mustBeValidDate: (val: string) =>
    !val ||
    (/^\d{4}-\d{2}-\d{2}$/.test(val) && !isNaN(new Date(val).getTime())) ||
    'エラー：無効な日付です',
  mustBeInValidDateOfBirthRange,
  mustBeValidEmail: (val: string) =>
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      val
    ) || 'メールアドレスを正しく入力してください',
  mustNotBeSame: (val: string, current: string, type?: string) => {
    const isError = val === current;

    return {
      isError,
      message: isError ? `同じ${type || '値'}は入力できません` : '',
    };
  },
  mustBeValidPhone,
  mustBeChecked: (val: boolean) => val,
  mustNotBeMisspelledDomain: (val: string) => {
    const domainCheckResult = emailChecker(val);

    if (domainCheckResult && domainCheckResult.length > 0) {
      return `ドメイン名（@以降の部分）が正しく入力されているかご確認ください`;
    } else {
      return undefined;
    }
  },
};

type Rule = keyof typeof rules;

const validator = {
  getRules: (_rules: Rule[]) =>
    _rules.reduce((final, rule) => {
      return {
        ...final,
        [rule]: rules[rule],
      };
    }, {}),
};

export default validator;
export { Rule, rules };
